import {
   IMetric,
   TPeriod,
} from '@forwardflow/features/campaign-flow/interfaces/campaign-flow.types'

export const PERIOD_DAYS: Record<TPeriod, number> = {
   '7d': 7,
   '30d': 30,
   '90d': 90,
   /* Lifetime: as far back as the daily stats go. */
   all: 730,
}
export const PERIOD_LABEL: Record<TPeriod, string> = {
   '7d': '7 days',
   '30d': '30 days',
   '90d': '90 days',
   all: 'All time',
}

/** 1,284 · 12.9K · 4.2M */
export const compact = (n: number): string => {
   if (Math.abs(n) >= 1e6)
      return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1).replace(/\.0$/, '')}M`
   if (Math.abs(n) >= 1e4)
      return `${(n / 1e3).toFixed(n >= 1e5 ? 0 : 1).replace(/\.0$/, '')}K`
   return Number.isInteger(n) ? n.toLocaleString() : n.toFixed(1)
}

/** 48 → "48 sec", 4200 → "1.2 hr", 285120 → "3.3 days" */
export const duration = (seconds: number): string => {
   if (seconds < 90) return `${Math.round(seconds)} sec`
   if (seconds < 3600) return `${Math.round(seconds / 60)} min`
   if (seconds < 86400)
      return `${(seconds / 3600).toFixed(1).replace(/\.0$/, '')} hr`
   return `${(seconds / 86400).toFixed(1).replace(/\.0$/, '')} days`
}

export const rate = (part: number, whole: number) =>
   whole ? (part / whole) * 100 : 0
export const percent = (value: number) =>
   `${value < 10 && value > 0 ? value.toFixed(1) : Math.round(value)}%`

export interface IDelta {
   /** Signed percent change vs the previous period. */
   change: number
   /** true = good, false = bad, null = no change. */
   good: boolean | null
   text: string
}

/** Change vs the previous period. Direction is judged by whether up is good for this number. */
export const delta = (
   m: Pick<IMetric, 'value' | 'previous'>,
   lowerIsBetter = false
): IDelta | null => {
   if (!m.previous) return null
   const change = ((m.value - m.previous) / m.previous) * 100
   if (Math.abs(change) < 0.5)
      return { change: 0, good: null, text: 'No change' }
   const up = change > 0
   return {
      change,
      good: lowerIsBetter ? !up : up,
      text: `${up ? '+' : '−'}${Math.abs(change) < 10 ? Math.abs(change).toFixed(1) : Math.round(Math.abs(change))}%`,
   }
}

/** Date label for point i of a 12-point trend ending today. */
export const trendLabel = (period: TPeriod, i: number, points = 12) => {
   const days = PERIOD_DAYS[period]
   const end = new Date()
   end.setDate(end.getDate() - Math.round(((points - 1 - i) * days) / points))
   return end.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
